import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TablePagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom'; // Import navigation hook

import { searchIncidents, chatWithData } from '../../api';

import ChatComponent from '../ChatComponent';

const IncidentsPage = () => {
    const [incidents, setIncidents] = useState([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [sourceId, setSourceId] = useState('');
    const [sourceType, setSourceType] = useState('');
    const [workflowId, setWorkflowId] = useState('');
    const [page, setPage] = useState(0); // TablePagination uses 0-based index
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    const navigate = useNavigate(); // Initialize navigation

    const fetchIncidents = async () => {
        try {
            const response = await searchIncidents({
                    startDate,
                    endDate,
                    sourceId,
                    sourceType,
                    workflowId,
                    page: page + 1, // Backend uses 1-based index
                    limit : rowsPerPage
            });
            setIncidents(response.data.incidents);
            setTotalRecords(response.data.totalCount);
        } catch (error) {
            console.error('Error fetching incidents:', error);
        }
    };

    const handleSearch = () => {
        setPage(0); // Reset page to first
        fetchIncidents();
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to first page
    };

    useEffect(() => {
        fetchIncidents();
    }, [page, rowsPerPage]);

    const handleRowClick = (incidentId) => {
        navigate(`/incident/${incidentId}`); // Navigate to detail page with incident ID
    };

    // Memoize the preprocessed data
    const preprocessedData = useMemo(() => {
        return incidents.map( incident => {  
            return {
                sourceType: incident.sourceType,
                sourceId: incident.sourceId['ATD Location ID'],
                createdAt: new Date(incident.createdAt).toLocaleString(),
                workflowName: incident.workflowId?.name || 'N/A',
                eventType: incident.eventId?.type || 'N/A'
            } 
          
         });
    }, [incidents]);

    return (
        <Container>
            <ChatComponent data={preprocessedData} queryApi={chatWithData} />
            <Typography variant="h4" gutterBottom>
                Incident Dashboard
            </Typography>
            <Grid container spacing={2} marginBottom={2}>
                {/* Filters */}
                {/* Same as before */}
            </Grid>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Source Type</TableCell>
                            <TableCell>Source ID</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell>Workflow Name</TableCell>
                            <TableCell>Event Type</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {incidents.length > 0 ? (
                            incidents.map((incident) => (
                                <TableRow
                                    key={incident._id}
                                    onClick={() => handleRowClick(incident._id)} // Add click handler
                                    style={{ cursor: 'pointer' }}
                                >
                                    <TableCell>{incident.sourceType}</TableCell>
                                    <TableCell>{incident.sourceId['ATD Location ID']}</TableCell>
                                    <TableCell>{new Date(incident.createdAt).toLocaleString()}</TableCell>
                                    <TableCell>{incident.workflowId?.name || 'N/A'}</TableCell>
                                    <TableCell>{incident.eventId?.type || 'N/A'}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No incidents found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* TablePagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRecords} // Total records returned from the API
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Container>
    );
};

export default IncidentsPage;
